import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Loader2, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ClientStatusBadge, RequestStatusBadge } from "@/components/Badges";
import { OrdersTable } from "@/components/OrdersTable";
import { ReconTable } from "@/components/ReconTable";
import { OrderDetailDialog } from "@/components/OrderDetailDialog";
import { NewOrderDialog } from "@/components/NewOrderDialog";
import { api, errMsg, money, fmtDate } from "@/lib/api";

function ProfileCard({ client, onSaved }) {
  const [f, setF] = useState({ name: client.name, phone: client.phone || "", notes: client.notes || "" });
  const [pw, setPw] = useState("");
  const save = async () => {
    try {
      await api.patch(`/admin/clients/${client.id}`, f);
      toast.success("Client saved");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const setPassword = async () => {
    try {
      await api.post(`/admin/clients/${client.id}/password`, { new_password: pw });
      toast.success("Password set — share it with the client");
      setPw("");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div className="space-y-4 rounded-lg border border-border bg-card p-6">
      <div className="space-y-1.5"><Label>Name</Label><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="client-name-input" /></div>
      <div className="space-y-1.5"><Label>Email</Label><Input value={client.email} disabled /></div>
      <div className="space-y-1.5"><Label>Phone</Label><Input value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} data-testid="client-phone-input" /></div>
      <div className="space-y-1.5"><Label>Private notes</Label><Textarea rows={4} value={f.notes} onChange={(e) => setF({ ...f, notes: e.target.value })} placeholder="Preferences, dosing notes, pickup info…" data-testid="client-notes-input" /></div>
      <Button onClick={save} data-testid="client-save-button">Save</Button>
      <div className="border-t border-border pt-4">
        <Label className="flex items-center gap-2"><KeyRound className="h-3.5 w-3.5" />{client.has_login ? "Reset password" : "Give login access (set password)"}</Label>
        <div className="mt-2 flex gap-2">
          <Input type="password" placeholder="min 6 characters" value={pw} onChange={(e) => setPw(e.target.value)} data-testid="client-password-input" />
          <Button variant="outline" disabled={pw.length < 6} onClick={setPassword} data-testid="client-password-button">Set</Button>
        </div>
      </div>
    </div>
  );
}

export default function ClientDetail() {
  const { id } = useParams();
  const [d, setD] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [creating, setCreating] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/clients/${id}`).then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);
  useEffect(() => {
    load();
  }, [load]);

  const setStatus = async (status) => {
    try {
      await api.patch(`/admin/clients/${id}`, { status });
      toast.success(`Client ${status}`);
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  if (!d) return <Loader2 className="h-5 w-5 animate-spin" />;
  const c = d.client;

  return (
    <div>
      <Link to="/admin/clients" className="mb-6 inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground" data-testid="client-back-link"><ArrowLeft className="h-4 w-4" />All clients</Link>
      <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between rise">
        <div>
          <p className="eyebrow mb-2">Client since {fmtDate(c.created_at)}{c.invite_code && ` · invite ${c.invite_code}`}{c.referred_by_name && ` · referred by ${c.referred_by_name}`}</p>
          <h1 className="flex items-center gap-3 text-3xl font-semibold sm:text-4xl" data-testid="client-detail-name">{c.name} <ClientStatusBadge status={c.status} testId="client-detail-status" /></h1>
        </div>
        <div className="flex flex-wrap gap-2">
          {c.status !== "approved" && <Button onClick={() => setStatus("approved")} data-testid="client-approve-button">Approve</Button>}
          {c.status !== "disabled" && <Button variant="outline" onClick={() => setStatus("disabled")} data-testid="client-disable-button">Disable</Button>}
          {c.status === "approved" && <Button variant="outline" onClick={() => setCreating(true)} className="gap-2" data-testid="client-new-order-button"><Plus className="h-4 w-4" />New order</Button>}
        </div>
      </div>
      <div className="mb-6 grid grid-cols-2 gap-4 lg:grid-cols-4">
        {[["Orders", d.stats.order_count], ["Total spent", money(d.stats.total_spent)], ["Outstanding", money(d.stats.outstanding)], ["Reconstitutions", d.stats.recon_count]].map(([l, v]) => (
          <div key={l} className="rounded-lg border border-border border-t-2 border-t-emerald-500 bg-card p-5" data-testid={`client-stat-${l.toLowerCase().replace(" ", "-")}`}>
            <p className="eyebrow text-muted-foreground">{l}</p><p className="mt-2 font-mono-data text-2xl">{v}</p>
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <ProfileCard key={c.id + c.name + (c.has_login ? 1 : 0)} client={c} onSaved={load} />
        <div className="lg:col-span-2">
          <Tabs defaultValue="orders">
            <TabsList>
              <TabsTrigger value="orders" data-testid="client-tab-orders">Orders</TabsTrigger>
              <TabsTrigger value="recon" data-testid="client-tab-recon">Reconstitutions</TabsTrigger>
              <TabsTrigger value="requests" data-testid="client-tab-requests">Requests</TabsTrigger>
            </TabsList>
            <TabsContent value="orders"><OrdersTable orders={d.orders} onOpen={setOpenId} showClient={false} /></TabsContent>
            <TabsContent value="recon"><ReconTable rows={d.reconstitutions} showClient={false} /></TabsContent>
            <TabsContent value="requests">
              {d.requests.length === 0 && <p className="py-10 text-center text-sm text-muted-foreground">No requests.</p>}
              {d.requests.map((r) => (
                <div key={r.id} className="mb-2 flex items-center justify-between rounded-md border border-border bg-card p-4 text-sm">
                  <span>{r.peptide_name} <span className="text-muted-foreground">{r.details}</span></span><RequestStatusBadge status={r.status} />
                </div>
              ))}
            </TabsContent>
          </Tabs>
        </div>
      </div>
      <OrderDetailDialog orderId={openId} onClose={() => setOpenId(null)} onChanged={load} />
      <NewOrderDialog open={creating} onClose={() => setCreating(false)} onCreated={load} presetClientId={c.id} />
    </div>
  );
}
